/**
 * One vocabulary for "how is this row doing".
 *
 * Five screens each shipped their own StatusPill, and between them "good" was
 * spelled bg-green-50, bg-green-100, bg-emerald-50 and bg-teal-50, while "bad"
 * was red-50, red-100 and rose-50. Nothing was broken — every pairing cleared
 * contrast — but a user moving from Attendance to Policies to Labour saw the
 * same idea in three different greens and read it as three different ideas.
 *
 * So tone is the thing components take, and the domain-status-to-tone mapping
 * lives here where it can be looked up rather than re-guessed. Statuses that
 * do not appear in the map fall back to neutral, which is the honest answer
 * for a status the design system has not been told about.
 */

export const TONES = ['neutral', 'success', 'warning', 'danger', 'info', 'brand'];

/** Soft fill + text, for pills and tinted tiles. */
export const TONE_SOFT = {
  neutral: 'bg-gray-100 text-gray-700',
  success: 'bg-success-bg text-success-fg',
  warning: 'bg-warning-bg text-warning-fg',
  danger: 'bg-danger-bg text-danger-fg',
  info: 'bg-info-bg text-info-fg',
  brand: 'bg-brand-100 text-brand-800'
};

/** Hairline + tint, for card and tile edges that need to carry the state. */
export const TONE_BORDER = {
  neutral: 'border-hairline bg-surface',
  success: 'border-success-border bg-success-bg/40',
  warning: 'border-warning-border bg-warning-bg/40',
  danger: 'border-danger-border bg-danger-bg/40',
  info: 'border-info-border bg-info-bg/40',
  brand: 'border-brand-100 bg-brand-50'
};

/** Solid fill, for the one selected item in a segmented control. */
export const TONE_SOLID = {
  neutral: 'bg-gray-900 text-white',
  success: 'bg-success text-white',
  warning: 'bg-warning text-white',
  danger: 'bg-danger text-white',
  info: 'bg-info text-white',
  brand: 'bg-brand-700 text-white'
};

/**
 * Domain status -> tone.
 *
 * Keyed by the uppercase status strings the API already returns. Grouped by
 * meaning rather than by module, because the same word means the same thing
 * whether it arrives from Labour or from Legal — and when a new module coins a
 * status, adding it here is what keeps it from inventing a colour.
 */
const STATUS_TONE = {
  // settled, went through
  ACTIVE: 'success',
  APPROVED: 'success',
  PRESENT: 'success',
  PUBLISHED: 'success',
  PAID: 'success',
  COMPLETED: 'success',
  CLOSED: 'success',
  VERIFIED: 'success',
  COMPLIANT: 'success',

  // waiting on a human
  PENDING: 'warning',
  ON_NOTICE: 'warning',
  SUBMITTED: 'warning',
  IN_REVIEW: 'warning',
  DRAFT: 'warning',
  PARTIAL: 'warning',
  HALF_DAY: 'warning',
  DUE: 'warning',

  // went wrong
  REJECTED: 'danger',
  ABSENT: 'danger',
  SUSPENDED: 'danger',
  OVERDUE: 'danger',
  BREACH: 'danger',
  NON_COMPLIANT: 'danger',
  FAILED: 'danger',

  // in flight, no judgement
  IN_PROGRESS: 'info',
  SCHEDULED: 'info',
  ON_LEAVE: 'info',
  LEAVE: 'info',
  OT_ONLY: 'info',
  CALL_IN: 'info',
  TRANSFERRED: 'info',

  // over, without judgement
  EXITED: 'neutral',
  ARCHIVED: 'neutral',
  WEEK_OFF: 'neutral',
  HOLIDAY: 'neutral',
  INACTIVE: 'neutral',
  CANCELLED: 'neutral',
  NOT_MARKED: 'neutral'
};

/** Tone for a domain status. Unknown statuses are neutral, never invented. */
export function toneForStatus(status) {
  if (!status) return 'neutral';
  return STATUS_TONE[String(status).toUpperCase().replace(/[\s-]+/g, '_')] ?? 'neutral';
}

/** ON_NOTICE -> "On notice". Used when a screen has no nicer label of its own. */
export function humanizeStatus(status) {
  if (!status) return '—';
  const s = String(status).replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}
